import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Paperclip, Upload, Trash2, Plus, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AttachmentData {
  id: string;
  file: File;
  permissions: 'all' | 'engineers' | 'client' | 'admin';
}

interface DialogAnexosProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (attachments: AttachmentData[]) => void;
}

export const DialogAnexos: React.FC<DialogAnexosProps> = ({
  open,
  onOpenChange,
  onSave
}) => {
  const [attachments, setAttachments] = useState<AttachmentData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const validateFile = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Arquivo muito grande",
        description: `O arquivo ${file.name} excede o limite de 100MB.`,
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    // You can expand this to show different icons for different file types
    switch (extension) {
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FileText className="w-8 h-8 text-blue-500" />;
      case 'xls':
      case 'xlsx':
        return <FileText className="w-8 h-8 text-green-500" />;
      default:
        return <FileText className="w-8 h-8 text-muted-foreground" />;
    }
  };

  const getPermissionLabel = (permission: string) => {
    switch (permission) {
      case 'all':
        return 'Todos';
      case 'engineers':
        return 'Apenas Engenheiros';
      case 'client':
        return 'Apenas Cliente';
      case 'admin':
        return 'Apenas Admin';
      default:
        return permission;
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateFile(files[i])) {
        validFiles.push(files[i]);
      }
    }

    const newAttachments: AttachmentData[] = validFiles.map(file => ({
      id: `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      permissions: 'all'
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const handlePermissionChange = (attachmentId: string, permission: 'all' | 'engineers' | 'client' | 'admin') => {
    setAttachments(prev => prev.map(attachment => 
      attachment.id === attachmentId ? { ...attachment, permissions: permission } : attachment
    ));
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleSave = () => {
    onSave(attachments);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setAttachments([]);
    onOpenChange(false);
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar Anexos</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {attachments.length === 0 ? (
            <div
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
              onClick={openFileSelector}
            >
              <Paperclip className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Clique para selecionar arquivos ou arraste arquivos aqui
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Máximo 100MB por arquivo • Qualquer tipo de arquivo
              </p>
              <Button variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Selecionar Arquivos
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="border rounded-lg p-4 flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {getFileIcon(attachment.file.name)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{attachment.file.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {attachment.file.type || 'Tipo desconhecido'} • {(attachment.file.size / 1024 / 1024).toFixed(2)}MB
                      </div>
                    </div>

                    <div className="flex-shrink-0 w-48">
                      <Label htmlFor={`permission-${attachment.id}`} className="text-xs">
                        Permissão
                      </Label>
                      <Select
                        value={attachment.permissions}
                        onValueChange={(value: 'all' | 'engineers' | 'client' | 'admin') => 
                          handlePermissionChange(attachment.id, value)
                        }
                      >
                        <SelectTrigger id={`permission-${attachment.id}`} className="text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="engineers">Apenas Engenheiros</SelectItem>
                          <SelectItem value="client">Apenas Cliente</SelectItem>
                          <SelectItem value="admin">Apenas Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={openFileSelector}
                  className="w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar mais arquivos
                </Button>
              </div>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={attachments.length === 0}>
            Salvar ({attachments.length} arquivo{attachments.length !== 1 ? 's' : ''})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};